import React, { useEffect, useMemo, useRef, useState } from "react";
import PageShell from "./PageShell";
import "./PortalUI.css";
import AudioPlayer from "./AudioPlayer";
import {
    listSharePointItems,
    uploadSharePointFile,
    getSharePointFileStreamUrl,
    buildSharePointFileStreamProxyUrl,
    createSharePointFolder,
    deleteSharePointItem,
    renameSharePointItem,
    getCurrentUser,
    setSharePointEpisodeOverride,
    clearSharePointEpisodeOverride,
} from "./api";

function formatSize(bytes) {
    const value = Number(bytes || 0);
    if (!value) return "—";
    const units = ["B", "KB", "MB", "GB", "TB"];
    let size = value;
    let idx = 0;
    while (size >= 1024 && idx < units.length - 1) {
        size /= 1024;
        idx += 1;
    }
    return `${size.toFixed(idx === 0 ? 0 : 1)} ${units[idx]}`;
}

function formatModified(value) {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function getFileExtension(name) {
    const raw = String(name || "").trim();
    const idx = raw.lastIndexOf(".");
    if (idx <= 0 || idx === raw.length - 1) return "";
    return raw.slice(idx + 1).toLowerCase();
}

function splitFileNameAndExtension(name) {
    const raw = String(name || "").trim();
    const idx = raw.lastIndexOf(".");
    if (idx <= 0 || idx === raw.length - 1) {
        return { baseName: raw, extensionWithDot: "" };
    }
    return {
        baseName: raw.slice(0, idx),
        extensionWithDot: raw.slice(idx),
    };
}

function getPreviewType(item) {
    const mime = String(item?.mime_type || "").toLowerCase();
    if (mime.startsWith("audio/")) return "audio";
    if (mime.startsWith("video/")) return "video";
    if (mime.startsWith("image/")) return "image";
    const ext = getFileExtension(item?.name);
    if (
        [
            "mp3",
            "m4a",
            "aac",
            "wav",
            "flac",
            "ogg",
            "oga",
            "opus",
            "mpga",
            "aif",
            "aiff",
            "wma",
        ].includes(ext)
    ) {
        return "audio";
    }
    if (["mp4"].includes(ext)) {
        return "video";
    }
    if (["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "heic"].includes(ext)) {
        return "image";
    }
    return "";
}

function isBlobUrl(value) {
    return String(value || "").startsWith("blob:");
}

function joinPath(basePath, segment) {
    if (!basePath || basePath === "/") return `/${segment}`;
    return `${basePath}/${segment}`.replace(/\/{2,}/g, "/");
}

function breadcrumbSegments(path) {
    const safe = String(path || "/");
    if (safe === "/") return [{ label: "Home", path: "/" }];

    const parts = safe.split("/").filter(Boolean);
    const out = [{ label: "Home", path: "/" }];
    let current = "";
    for (const part of parts) {
        current += `/${part}`;
        out.push({ label: part, path: current });
    }
    return out;
}

function isExternalFileDrag(e) {
    const types = Array.from(e?.dataTransfer?.types || []);
    return types.includes("Files");
}

function isEpisodesFolderPath(path) {
    const parts = String(path || "")
        .split("/")
        .filter(Boolean);
    if (!parts.length) return false;
    return String(parts[parts.length - 1] || "").toLowerCase() === "episodes";
}

function FileExplorer({ onLogout, embedded = false }) {
    const [currentPath, setCurrentPath] = useState("/");
    const [currentAccessLevel, setCurrentAccessLevel] = useState("read");
    const [canWrite, setCanWrite] = useState(false);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [busyAction, setBusyAction] = useState(false);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [sortKey, setSortKey] = useState(null);
    const [sortDirection, setSortDirection] = useState("asc");
    const [isAdminUser, setIsAdminUser] = useState(false);
    const [activeActionMenuId, setActiveActionMenuId] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [folderDropTargetId, setFolderDropTargetId] = useState("");
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState("");
    const [previewName, setPreviewName] = useState("");
    const [previewType, setPreviewType] = useState("");
    const [previewUrl, setPreviewUrl] = useState("");
    const [toasts, setToasts] = useState([]);
    const dragDepthRef = useRef(0);
    const draggedItemRef = useRef(null);
    const fileInputRef = useRef(null);
    const uploadToastIdRef = useRef(null);

    const addToast = (message, type = "info", timeoutMs = 5000) => {
        const id = Date.now() + Math.random();
        setToasts((current) => [...current, { id, message, type }]);
        if (timeoutMs && timeoutMs > 0) {
            window.setTimeout(() => {
                setToasts((current) => current.filter((t) => t.id !== id));
            }, timeoutMs);
        }
        return id;
    };

    const removeToast = (id) => {
        setToasts((current) => current.filter((t) => t.id !== id));
    };

    const updateToast = (id, message, type) => {
        if (!id) return;
        setToasts((current) =>
            current.map((t) =>
                t.id === id
                    ? {
                          ...t,
                          message: message ?? t.message,
                          ...(type ? { type } : {}),
                      }
                    : t
            )
        );
    };

    async function load(path) {
        setLoading(true);
        setError("");
        try {
            const res = await listSharePointItems(path);
            if (!res || !res.ok) {
                throw new Error(res?.message || "Failed to load SharePoint files");
            }
            setItems(Array.isArray(res.items) ? res.items : []);
            setCurrentPath(res.path || "/");
            setCurrentAccessLevel(res.access_level || "read");
            setCanWrite(Boolean(res.can_write));
        } catch (err) {
            setError(err.message || "Failed to load SharePoint files");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load("/");
    }, []);

    useEffect(() => {
        const handleDocumentClick = () => {
            setActiveActionMenuId(null);
        };
        document.addEventListener("click", handleDocumentClick);
        return () => document.removeEventListener("click", handleDocumentClick);
    }, []);

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (!uploading) return;
            e.preventDefault();
            e.returnValue =
                "An upload is still in progress. If you leave now it may be interrupted.";
            return e;
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [uploading]);

    useEffect(() => {
        let cancelled = false;
        async function loadCurrentUser() {
            try {
                const res = await getCurrentUser();
                if (cancelled) return;
                setIsAdminUser(res?.ok && res?.user?.role === "admin");
            } catch {
                if (!cancelled) setIsAdminUser(false);
            }
        }
        loadCurrentUser();
        return () => {
            cancelled = true;
        };
    }, []);

    const breadcrumbs = useMemo(
        () => breadcrumbSegments(currentPath),
        [currentPath]
    );
    const showEpisodeColumn = useMemo(
        () => isEpisodesFolderPath(currentPath),
        [currentPath]
    );

    const sortedItems = useMemo(() => {
        const filtered = [...items].filter((item) => {
            if (!search.trim()) return true;
            return String(item.name || "")
                .toLowerCase()
                .includes(search.trim().toLowerCase());
        });

        const compareValues = (a, b) => {
            if (a === b) return 0;
            if (a === null || a === undefined || a === "") return 1;
            if (b === null || b === undefined || b === "") return -1;
            if (typeof a === "number" && typeof b === "number") return a - b;
            return String(a).localeCompare(String(b), undefined, {
                numeric: true,
                sensitivity: "base",
            });
        };

        const normalizedSortKey =
            !showEpisodeColumn && sortKey === "episode" ? null : sortKey;
        const effectiveSortKey = normalizedSortKey || "name";
        const effectiveSortDirection = normalizedSortKey ? sortDirection : "asc";

        const getSortValue = (item) => {
            if (effectiveSortKey === "episode") return String(item.episode_code || "");
            if (effectiveSortKey === "size") return Number(item.size || 0);
            if (effectiveSortKey === "modified") {
                const ts = item.last_modified ? new Date(item.last_modified).getTime() : 0;
                return Number.isFinite(ts) ? ts : 0;
            }
            return String(item.name || "");
        };

        return filtered.sort((a, b) => {
            if (a.type !== b.type) {
                return a.type === "folder" ? -1 : 1;
            }
            const left = getSortValue(a);
            const right = getSortValue(b);
            const base = compareValues(left, right);
            if (base !== 0) {
                return effectiveSortDirection === "asc" ? base : -base;
            }
            return String(a.name || "").localeCompare(String(b.name || ""), undefined, {
                numeric: true,
                sensitivity: "base",
            });
        });
    }, [items, search, showEpisodeColumn, sortDirection, sortKey]);

    const toggleSort = (key) => {
        if (sortKey !== key) {
            setSortKey(key);
            setSortDirection("asc");
            return;
        }
        if (sortDirection === "asc") {
            setSortDirection("desc");
            return;
        }
        setSortKey(null);
        setSortDirection("asc");
    };

    const sortIndicator = (key) => {
        if (sortKey !== key) return "";
        return sortDirection === "asc" ? " ↑" : " ↓";
    };

    const openFolder = (item) => {
        if (!item || item.type !== "folder") return;
        setSearch("");
        load(joinPath(currentPath, item.name));
    };

    const goToPath = (path) => {
        setSearch("");
        load(path || "/");
    };

    const goUp = () => {
        if (currentPath === "/") return;
        const parent = currentPath.split("/").filter(Boolean).slice(0, -1);
        goToPath(parent.length ? `/${parent.join("/")}` : "/");
    };

    const handleUploadFilesToPath = async (targetPath, files) => {
        if (!canWrite) return;
        if (uploading) {
            addToast(
                "A file upload is already in progress. Please wait before starting a second upload.",
                "warning",
                5000
            );
            return;
        }
        if (!files || files.length === 0) return;
        const uploadPath = String(targetPath || currentPath || "/");
        const totalFiles = files.length;
        const totalBytes = files.reduce(
            (sum, file) => sum + Number(file?.size || 0),
            0
        );
        let uploadedCount = 0;
        let uploadedBytes = 0;
        setUploading(true);
        setUploadProgress(0);
        setError("");
        if (!uploadToastIdRef.current) {
            uploadToastIdRef.current = addToast(
                "Uploading files… 0% You can keep using the portal.",
                "info",
                0
            );
        }
        try {
            for (const file of files) {
                const fileBytes = Number(file?.size || 0);
                const res = await uploadSharePointFile(uploadPath, file, {
                    onProgress: ({ loaded }) => {
                        if (totalBytes > 0) {
                            const overallLoaded = Math.min(
                                totalBytes,
                                uploadedBytes + Math.max(0, Number(loaded || 0))
                            );
                            setUploadProgress(
                                Math.round((overallLoaded / totalBytes) * 100)
                            );
                            updateToast(
                                uploadToastIdRef.current,
                                `Uploading files… ${Math.round(
                                    (overallLoaded / totalBytes) * 100
                                )}% You can keep using the portal.`
                            );
                            return;
                        }
                        setUploadProgress(
                            Math.round((uploadedCount / totalFiles) * 100)
                        );
                    },
                });
                if (!res || !res.ok) {
                    throw new Error(
                        res?.message || `Failed to upload file: ${file?.name || "unknown"}`
                    );
                }
                uploadedCount += 1;
                uploadedBytes += fileBytes;
                const percent =
                    totalBytes > 0
                        ? Math.round((uploadedBytes / totalBytes) * 100)
                        : Math.round((uploadedCount / totalFiles) * 100);
                setUploadProgress(percent);
                updateToast(
                    uploadToastIdRef.current,
                    `Uploading files… ${percent}% You can keep using the portal.`
                );
            }
            await load(currentPath);
            if (uploadToastIdRef.current) {
                removeToast(uploadToastIdRef.current);
                uploadToastIdRef.current = null;
            }
            addToast("File upload completed successfully.", "success", 5000);
        } catch (err) {
            setError(err.message || "Failed to upload file");
            if (uploadToastIdRef.current) {
                removeToast(uploadToastIdRef.current);
                uploadToastIdRef.current = null;
            }
            addToast(err.message || "File upload failed. Please try again.", "error", 0);
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    const handleUploadFiles = async (files) => {
        await handleUploadFilesToPath(currentPath, files);
    };

    const handleDownload = async (item) => {
        if (!item?.id || item.type !== "file") return;
        setError("");
        const urlRes = await getSharePointFileStreamUrl(item.id);
        if (!urlRes || !urlRes.ok || !urlRes.stream_url) {
            setError(urlRes?.message || "Failed to download file");
            return;
        }

        const anchor = document.createElement("a");
        anchor.href = String(urlRes.stream_url);
        anchor.download = item.name || "download.bin";
        anchor.rel = "noopener noreferrer";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
    };

    const closePreview = () => {
        if (isBlobUrl(previewUrl)) {
            window.URL.revokeObjectURL(previewUrl);
        }
        setPreviewUrl("");
        setPreviewOpen(false);
        setPreviewLoading(false);
        setPreviewError("");
        setPreviewName("");
        setPreviewType("");
    };

    const handlePreview = async (item) => {
        const type = getPreviewType(item);
        if (!item?.id || item.type !== "file" || !type) return;

        if (isBlobUrl(previewUrl)) {
            window.URL.revokeObjectURL(previewUrl);
        }
        setPreviewOpen(true);
        setPreviewLoading(true);
        setPreviewError("");
        setPreviewName(item.name || "Preview");
        setPreviewType(type);
        setPreviewUrl("");

        if (type === "video") {
            const urlRes = await getSharePointFileStreamUrl(item.id);
            if (!urlRes || !urlRes.ok) {
                setPreviewLoading(false);
                setPreviewError(urlRes?.message || "Failed to load preview");
                return;
            }
            setPreviewUrl(buildSharePointFileStreamProxyUrl(item.id));
            setPreviewLoading(false);
            return;
        }

        if (item.download_url) {
            setPreviewUrl(String(item.download_url));
            setPreviewLoading(false);
            return;
        }

        const urlRes = await getSharePointFileStreamUrl(item.id);
        if (!urlRes || !urlRes.ok || !urlRes.stream_url) {
            setPreviewLoading(false);
            setPreviewError(urlRes?.message || "Failed to load preview");
            return;
        }
        setPreviewUrl(String(urlRes.stream_url));
        setPreviewLoading(false);
    };

    useEffect(() => {
        return () => {
            if (isBlobUrl(previewUrl)) {
                window.URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    useEffect(() => {
        if (!previewOpen) return undefined;

        const handleKeyDown = (e) => {
            if (e.key !== "Escape") return;
            e.preventDefault();
            closePreview();
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [previewOpen, previewUrl]);

    const handleCreateFolder = async () => {
        if (!canWrite) return;
        const name = window.prompt("Folder name");
        if (!name || !name.trim()) return;

        setBusyAction(true);
        setError("");
        try {
            const res = await createSharePointFolder(currentPath, name.trim());
            if (!res || !res.ok) {
                throw new Error(res?.message || "Failed to create folder");
            }
            await load(currentPath);
        } catch (err) {
            setError(err.message || "Failed to create folder");
        } finally {
            setBusyAction(false);
        }
    };

    const handleDelete = async (item) => {
        if (!canWrite) return;
        if (!item?.id) return;
        const ok = window.confirm(
            `Delete ${item.type} "${item.name}"? This cannot be undone.`
        );
        if (!ok) return;

        setBusyAction(true);
        setError("");
        try {
            const res = await deleteSharePointItem(item.id);
            if (!res || !res.ok) {
                throw new Error(res?.message || "Failed to delete item");
            }
            await load(currentPath);
        } catch (err) {
            setError(err.message || "Failed to delete item");
        } finally {
            setBusyAction(false);
        }
    };

    const handleRename = async (item) => {
        if (!canWrite) return;
        if (!item?.id) return;
        const isFile = item?.type === "file";
        const { baseName, extensionWithDot } = splitFileNameAndExtension(item?.name);
        const hasLockedExtension = isFile && Boolean(extensionWithDot);
        const newName = window.prompt(
            hasLockedExtension ? `New name (without ${extensionWithDot})` : "New name",
            hasLockedExtension ? baseName : item.name
        );
        if (newName === null) return;
        const trimmedName = newName.trim();
        if (!trimmedName) return;
        const trimmedWithoutMatchingExt =
            hasLockedExtension &&
            trimmedName.toLowerCase().endsWith(extensionWithDot.toLowerCase())
                ? trimmedName.slice(0, -extensionWithDot.length).trim()
                : trimmedName;
        if (!trimmedWithoutMatchingExt) return;
        const finalName = hasLockedExtension
            ? `${trimmedWithoutMatchingExt}${extensionWithDot}`
            : trimmedWithoutMatchingExt;

        setBusyAction(true);
        setError("");
        try {
            const res = await renameSharePointItem(item.id, finalName);
            if (!res || !res.ok) {
                throw new Error(res?.message || "Failed to rename item");
            }
            await load(currentPath);
        } catch (err) {
            setError(err.message || "Failed to rename item");
        } finally {
            setBusyAction(false);
        }
    };

    const handleSetEpisodeOverride = async (item) => {
        if (!isAdminUser || !item || item.type !== "folder") return;
        const current = String(item.episode_code || "").trim();
        const nextValue = window.prompt("Episode code (e.g. S01E01 or E01)", current);
        if (nextValue === null) return;
        const episodeCode = String(nextValue || "").trim();
        if (!episodeCode) return;

        const folderPath = joinPath(currentPath, item.name);
        setBusyAction(true);
        setError("");
        try {
            const res = await setSharePointEpisodeOverride(folderPath, episodeCode);
            if (!res?.ok) {
                throw new Error(res?.message || "Failed to set episode override");
            }
            await load(currentPath);
        } catch (err) {
            setError(err.message || "Failed to set episode override");
        } finally {
            setBusyAction(false);
        }
    };

    const handleClearEpisodeOverride = async (item) => {
        if (!isAdminUser || !item || item.type !== "folder") return;
        if (String(item.episode_code_source || "") !== "override") return;
        const folderPath = joinPath(currentPath, item.name);
        setBusyAction(true);
        setError("");
        try {
            const res = await clearSharePointEpisodeOverride(folderPath);
            if (!res?.ok) {
                throw new Error(res?.message || "Failed to clear episode override");
            }
            await load(currentPath);
        } catch (err) {
            setError(err.message || "Failed to clear episode override");
        } finally {
            setBusyAction(false);
        }
    };

    const handleDragEnter = (e) => {
        if (!canWrite) return;
        if (!isExternalFileDrag(e)) return;
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current += 1;
        setIsDragOver(true);
    };

    const handleDragOver = (e) => {
        if (!canWrite) return;
        if (!isExternalFileDrag(e)) return;
        e.preventDefault();
        e.stopPropagation();
        if (!isDragOver) setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        if (!canWrite) return;
        if (!isExternalFileDrag(e) && !isDragOver) return;
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) {
            setIsDragOver(false);
            setFolderDropTargetId("");
        }
    };

    const handleDrop = async (e) => {
        if (!canWrite) return;
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current = 0;
        setIsDragOver(false);
        setFolderDropTargetId("");

        const droppedFiles = Array.from(e.dataTransfer?.files || []);
        if (!droppedFiles.length) return;
        await handleUploadFiles(droppedFiles);
    };

    const handleFolderDrop = async (e, item) => {
        if (!canWrite) return;
        if (!item || item.type !== "folder") return;
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current = 0;
        setIsDragOver(false);
        setFolderDropTargetId("");

        const droppedFiles = Array.from(e.dataTransfer?.files || []);
        if (droppedFiles.length) {
            const targetPath = joinPath(currentPath, item.name);
            await handleUploadFilesToPath(targetPath, droppedFiles);
            return;
        }

        const draggedId =
            String(e.dataTransfer?.getData("application/x-portal-item-id") || "").trim() ||
            String(draggedItemRef.current?.id || "").trim();
        const draggedName = String(draggedItemRef.current?.name || "").trim();
        if (!draggedId || !draggedName) return;
        if (draggedId === String(item.id || "")) return;

        setBusyAction(true);
        setError("");
        try {
            const res = await renameSharePointItem(draggedId, draggedName, item.id);
            if (!res?.ok) {
                throw new Error(res?.message || "Failed to move item");
            }
            await load(currentPath);
        } catch (err) {
            setError(err.message || "Failed to move item");
        } finally {
            setBusyAction(false);
            draggedItemRef.current = null;
        }
    };

    const handleItemDragStart = (e, item) => {
        if (!canWrite) return;
        if (!item?.id) return;
        draggedItemRef.current = item;
        if (e.dataTransfer) {
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("application/x-portal-item-id", String(item.id));
            e.dataTransfer.setData("text/plain", String(item.id));
        }
    };

    const handleItemDragEnd = () => {
        draggedItemRef.current = null;
        setFolderDropTargetId("");
    };

    const renderRowActions = (item) => {
        const canSetOverride =
            showEpisodeColumn &&
            isAdminUser &&
            item.type === "folder" &&
            !item.episode_code;
        const canEditOverride =
            showEpisodeColumn &&
            isAdminUser &&
            item.type === "folder" &&
            item.episode_code_source === "override" &&
            item.episode_code;

        const hasAnyAction =
            (item.type === "file" && true) || // download for files
            canWrite ||
            canSetOverride ||
            canEditOverride;
        if (!hasAnyAction) return null;
        const isOpen = activeActionMenuId === item.id;

        return (
            <>
                <div
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                    }}
                >
                    <span
                        aria-hidden="true"
                        style={{
                            width: 1,
                            height: 18,
                            background: "rgba(148, 163, 184, 0.35)",
                            display: "inline-block",
                        }}
                    />
                    <button
                        type="button"
                        style={{
                            border: "none",
                            background: "transparent",
                            color: "rgba(148, 163, 184, 0.9)",
                            cursor: "pointer",
                            fontSize: 14,
                            fontWeight: 700,
                            lineHeight: 1,
                            width: 16,
                            height: 16,
                            borderRadius: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 0,
                            opacity: 0.85,
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setActiveActionMenuId((current) =>
                                current === item.id ? null : item.id
                            );
                        }}
                        aria-label="Open actions menu"
                    >
                        ⋯
                    </button>
                </div>
                {isOpen && (
                <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        position: "absolute",
                        right: 0,
                        bottom: "calc(100% + 4px)",
                        zIndex: 20,
                        minWidth: 140,
                        maxHeight: 240,
                        overflowY: "auto",
                        border: "1px solid #334155",
                        borderRadius: 8,
                        background: "#0f172a",
                        padding: 6,
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                    }}
                >
                    {item.type === "file" && Boolean(getPreviewType(item)) && (
                        <button
                            type="button"
                            className="btn btn--secondary btn--tiny"
                            onClick={() => {
                                setActiveActionMenuId(null);
                                handlePreview(item);
                            }}
                            disabled={busyAction}
                        >
                            Preview
                        </button>
                    )}
                    {item.type === "file" && (
                        <button
                            type="button"
                            className="btn btn--secondary btn--tiny"
                            onClick={() => {
                                setActiveActionMenuId(null);
                                handleDownload(item);
                            }}
                            disabled={busyAction}
                        >
                            Download
                        </button>
                    )}
                    {canWrite && (
                        <button
                            type="button"
                            className="btn btn--secondary btn--tiny"
                            onClick={() => {
                                setActiveActionMenuId(null);
                                handleRename(item);
                            }}
                            disabled={busyAction}
                        >
                            Rename
                        </button>
                    )}
                    {canWrite && (
                        <button
                            type="button"
                            className="btn btn--danger btn--tiny"
                            onClick={() => {
                                setActiveActionMenuId(null);
                                handleDelete(item);
                            }}
                            disabled={busyAction}
                        >
                            Delete
                        </button>
                    )}
                    {canSetOverride && (
                        <button
                            type="button"
                            className="btn btn--secondary btn--tiny"
                            onClick={() => {
                                setActiveActionMenuId(null);
                                handleSetEpisodeOverride(item);
                            }}
                            disabled={busyAction}
                        >
                            Set Episode
                        </button>
                    )}
                    {canEditOverride && (
                        <>
                            <button
                                type="button"
                                className="btn btn--secondary btn--tiny"
                                onClick={() => {
                                    setActiveActionMenuId(null);
                                    handleSetEpisodeOverride(item);
                                }}
                                disabled={busyAction}
                            >
                                Edit Episode
                            </button>
                            <button
                                type="button"
                                className="btn btn--secondary btn--tiny"
                                onClick={() => {
                                    setActiveActionMenuId(null);
                                    handleClearEpisodeOverride(item);
                                }}
                                disabled={busyAction}
                            >
                                Clear Episode
                            </button>
                        </>
                    )}
                </div>
                )}
            </>
        );
    };

    return (
        <PageShell embedded={embedded} onLogout={onLogout}>
            <div className="portal-page">
                <div className="portal-page__header">
                    <div>
                        <h2 className="portal-page__title">File Explorer</h2>
                        <p className="portal-page__subtitle">
                            Browse, upload and download files.
                        </p>
                    </div>
                </div>

                <div
                    className={
                        "card file-explorer" +
                        (isDragOver ? " file-explorer--drag-over" : "")
                    }
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    {isDragOver && canWrite && (
                        <div className="file-explorer__drop-hint">
                            Drop files to upload into this folder
                        </div>
                    )}
                    <div className="file-explorer__command-bar">
                        {canWrite && (
                            <>
                                <button
                                    type="button"
                                    className="btn btn--primary btn--small"
                                    onClick={handleCreateFolder}
                                    disabled={loading || uploading || busyAction}
                                >
                                    New folder
                                </button>

                                <button
                                    type="button"
                                    className="btn btn--secondary btn--small"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={loading || uploading || busyAction}
                                >
                                    {uploading ? `Uploading… ${uploadProgress}%` : "Upload files"}
                                </button>

                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    onChange={(e) => {
                                        const picked = Array.from(e.target.files || []);
                                        // Allow selecting the same file again if needed.
                                        e.target.value = "";
                                        if (picked.length) {
                                            handleUploadFiles(picked);
                                        }
                                    }}
                                    className="file-explorer__upload-input"
                                    disabled={loading || uploading || busyAction}
                                />
                            </>
                        )}

                        <button
                            type="button"
                            className="btn btn--secondary btn--small"
                            onClick={() => load(currentPath)}
                            disabled={loading || uploading || busyAction}
                        >
                            Refresh
                        </button>
                    </div>

                    <div className="file-explorer__path-bar">
                        <button
                            type="button"
                            className="btn btn--secondary btn--small"
                            onClick={goUp}
                            disabled={currentPath === "/" || loading}
                        >
                            Up
                        </button>

                        <div className="file-explorer__breadcrumbs">
                            {breadcrumbs.map((crumb, index) => (
                                <button
                                    key={crumb.path}
                                    type="button"
                                    className={
                                        "file-explorer__crumb" +
                                        (index === breadcrumbs.length - 1
                                            ? " file-explorer__crumb--active"
                                            : "")
                                    }
                                    onClick={() => goToPath(crumb.path)}
                                    disabled={loading || index === breadcrumbs.length - 1}
                                >
                                    {crumb.label}
                                </button>
                            ))}
                        </div>

                        <input
                            type="text"
                            className="file-explorer__search"
                            placeholder="Search this folder"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>

                    {!canWrite && (
                        <p className="text-description file-explorer__selected-file">
                            This folder is read-only ({currentAccessLevel} access).
                        </p>
                    )}

                    {error && <div className="alert alert--error">{error}</div>}
                    {loading && <p>Loading files…</p>}

                    {!loading && sortedItems.length === 0 && (
                        <p className="text-description">No files or folders in this location.</p>
                    )}

                    {!loading && sortedItems.length > 0 && (
                        <table className="change-log-table file-explorer__table">
                            {showEpisodeColumn ? (
                                <colgroup>
                                    <col style={{ width: "47%" }} />
                                    <col style={{ width: "16%" }} />
                                    <col style={{ width: "9%" }} />
                                    <col style={{ width: "23%" }} />
                                    <col style={{ width: "5%" }} />
                                </colgroup>
                            ) : (
                                <colgroup>
                                    <col style={{ width: "66%" }} />
                                    <col style={{ width: "9%" }} />
                                    <col style={{ width: "20%" }} />
                                    <col style={{ width: "5%" }} />
                                </colgroup>
                            )}
                            <thead>
                                <tr>
                                    <th>
                                        <button
                                            type="button"
                                            className="file-explorer__name-link"
                                            onClick={() => toggleSort("name")}
                                        >
                                            {`Name${sortIndicator("name")}`}
                                        </button>
                                    </th>
                                    {showEpisodeColumn && (
                                        <th className="file-explorer__col-mobile-hide">
                                            <button
                                                type="button"
                                                className="file-explorer__name-link"
                                                onClick={() => toggleSort("episode")}
                                            >
                                                {`Episode${sortIndicator("episode")}`}
                                            </button>
                                        </th>
                                    )}
                                    <th className="file-explorer__col-mobile-hide">
                                        <button
                                            type="button"
                                            className="file-explorer__name-link"
                                            onClick={() => toggleSort("size")}
                                        >
                                            {`Size${sortIndicator("size")}`}
                                        </button>
                                    </th>
                                    <th className="file-explorer__col-mobile-hide">
                                        <button
                                            type="button"
                                            className="file-explorer__name-link"
                                            onClick={() => toggleSort("modified")}
                                        >
                                            {`Modified${sortIndicator("modified")}`}
                                        </button>
                                    </th>
                                    <th aria-label="Actions" />
                                </tr>
                            </thead>
                            <tbody>
                                {sortedItems.map((item) => (
                                    <tr
                                        key={item.id}
                                        className={
                                            item.type === "folder" &&
                                            String(item.id || "") === folderDropTargetId
                                                ? "file-explorer__row file-explorer__row--drop-target"
                                                : "file-explorer__row"
                                        }
                                        draggable={canWrite}
                                        onDragStart={(e) => handleItemDragStart(e, item)}
                                        onDragEnd={handleItemDragEnd}
                                        onDragEnter={(e) => {
                                            if (!canWrite) return;
                                            if (!item || item.type !== "folder") return;
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setFolderDropTargetId(String(item.id || ""));
                                        }}
                                        onDragOver={(e) => {
                                            if (!canWrite) return;
                                            if (!item || item.type !== "folder") return;
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
                                            setFolderDropTargetId(String(item.id || ""));
                                        }}
                                        onDragLeave={(e) => {
                                            if (!item || item.type !== "folder") return;
                                            const next = e.relatedTarget;
                                            if (next && e.currentTarget.contains(next)) return;
                                            setFolderDropTargetId((current) =>
                                                current === String(item.id || "") ? "" : current
                                            );
                                        }}
                                        onDrop={(e) => {
                                            if (!item || item.type !== "folder") return;
                                            handleFolderDrop(e, item);
                                        }}
                                    >
                                        <td>
                                            <div className="file-explorer__name-cell">
                                                <span className="file-explorer__name-icon">
                                                    {item.type === "folder" ? "📁" : "📄"}
                                                </span>
                                                {item.type === "folder" ? (
                                                    <button
                                                        type="button"
                                                        className="file-explorer__name-link"
                                                        onClick={() => openFolder(item)}
                                                        disabled={busyAction}
                                                    >
                                                        {item.name}
                                                    </button>
                                                ) : (
                                                    <span>{item.name}</span>
                                                )}
                                            </div>
                                        </td>
                                        {showEpisodeColumn && (
                                            <td className="file-explorer__col-mobile-hide">
                                                <span>{item.episode_code || "—"}</span>
                                            </td>
                                        )}
                                        <td className="file-explorer__col-mobile-hide">
                                            {item.type === "file" ? formatSize(item.size) : "—"}
                                        </td>
                                        <td className="file-explorer__col-mobile-hide">
                                            {formatModified(item.last_modified)}
                                        </td>
                                        <td>
                                            <div
                                                className="file-explorer__actions"
                                                style={{
                                                    position: "relative",
                                                    display: "flex",
                                                    justifyContent: "flex-end",
                                                    overflow: "visible",
                                                }}
                                            >
                                                {renderRowActions(item)}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {previewOpen && (
                <div className="modal-overlay">
                    <div className="modal" style={{ maxWidth: 560 }}>
                        <div className="modal__header">
                            <div>
                                <h3 className="modal__title">Preview</h3>
                                <p className="modal__subtitle">{previewName || "File preview"}</p>
                            </div>
                            <button className="modal__close-btn" onClick={closePreview}>
                                ✕
                            </button>
                        </div>
                        <div className="modal__body">
                            {previewLoading && <p>Loading preview...</p>}
                            {!previewLoading && previewError && (
                                <div className="alert alert--error">{previewError}</div>
                            )}
                            {!previewLoading &&
                                !previewError &&
                                previewUrl &&
                                previewType === "audio" && (
                                    <AudioPlayer src={previewUrl} />
                                )}
                            {!previewLoading &&
                                !previewError &&
                                previewUrl &&
                                previewType === "video" && (
                                    <video
                                        src={previewUrl}
                                        controls
                                        preload="metadata"
                                        style={{
                                            width: "100%",
                                            maxHeight: "70vh",
                                            borderRadius: 8,
                                            background: "#000",
                                        }}
                                    />
                                )}
                            {!previewLoading &&
                                !previewError &&
                                previewUrl &&
                                previewType === "image" && (
                                    <img
                                        src={previewUrl}
                                        alt={previewName || "Image preview"}
                                        style={{
                                            width: "100%",
                                            maxHeight: "70vh",
                                            objectFit: "contain",
                                            borderRadius: 8,
                                        }}
                                    />
                                )}
                        </div>
                        <div className="modal__footer">
                            <span />
                            <button type="button" className="btn btn--secondary" onClick={closePreview}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {toasts.length > 0 && (
                <div className="toast-container">
                    {toasts.map((toast) => (
                        <div key={toast.id} className={`toast toast--${toast.type || "info"}`}>
                            <span className="toast__message">{toast.message}</span>
                            <button
                                type="button"
                                className="toast__close"
                                onClick={() => removeToast(toast.id)}
                                aria-label="Dismiss"
                            >
                                ×
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </PageShell>
    );
}

export default FileExplorer;
